//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { useAxios } from '../useAxios';
import { AxiosError } from 'axios';
import { ReactQueryConfig } from 'hooks/hooks.types';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { UseQueryOptions, useQueries } from 'react-query';
import { keysToCamelCase } from 'utils/formatters';

type QueryItem<R> = {
  key: string[];
  path: string;
  config?: ReactQueryConfig<R>;
};

/**
 * A custom hook to manage multiple Axios-based queries using React Query.
 *
 * @param queries - An array of query configurations.
 * @param combineLogicFn - Function to combine data across queries.
 * @param uniqueEntityResolver - Function to identify unique entities.
 * @returns The combined results of all queries along with their statuses.
 */
type UseAxiosQueriesProps<R> = {
  queries: QueryItem<R>[];
  combineLogicFn?: (allData: R[][], uniqueEntityResolver: (item: R) => string | number) => R[];
  uniqueEntityResolver?: (item: R) => string | number;
};

export function useAxiosQueries<R>({ queries, combineLogicFn, uniqueEntityResolver }: UseAxiosQueriesProps<R>) {
  const { enqueueSnackbar } = useSnackbar();
  const { axiosRequest } = useAxios();

  const fetchData = async (
    path: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: Record<string, unknown>,
    mapper?: (data: unknown) => R
  ): Promise<R> => {
    const response = await axiosRequest({ method, path, body });
    let data = response?.data;

    if (mapper) {
      data = mapper(data);
    }

    return keysToCamelCase(data) as R;
  };

  // Generate query configurations for useQueries
  const queryConfigs: UseQueryOptions<R, AxiosError>[] = queries.map(({ key, path, config }) => ({
    queryKey: key,
    queryFn: async () => fetchData(path, config?.method || 'GET', config?.body || undefined, config?.mapper),
    onError: (error: AxiosError) => {
      enqueueSnackbar(`Something went wrong with getting data from the database: ${error?.message}`, {
        variant: 'error',
      });
    },
    retry: config?.retry ?? 2,
    enabled: config?.enabled ?? true,
    ...config,
  }));

  const results = useQueries(queryConfigs);

  const combinedData = useMemo(() => {
    // Map results to extract data arrays or default to an empty array
    const allData: R[][] = results.map((result) => (result.data as R[]) || []);

    if (!uniqueEntityResolver || !combineLogicFn) {
      return allData.flat(); // Default to flat merging if no logic is provided
    }

    return combineLogicFn(allData, uniqueEntityResolver);
  }, [results, combineLogicFn, uniqueEntityResolver]);

  return {
    combinedData,
    results,
    isLoading: results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
    isError: results.some((result) => result.isError),
    isSuccess: results.every((result) => result.isSuccess),
  };
}
